import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import {
  validateHourlyRateForm,
  isHourlyRateLabelUnique,
  type HourlyRateFormValues,
} from './validators';
import { useResponsive } from '../../hooks/useResponsive';

export interface ExistingHourlyRate {
  id: string;
  label: string;
  amount: number;
}

interface Props {
  open: boolean;
  editing: ExistingHourlyRate | null;
  allRates: { id: string; label: string }[];
  onSubmit: (values: { label: string; amount: number }) => Promise<void>;
  onClose: () => void;
}

export default function HourlyRateDialog({
  open,
  editing,
  allRates,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setError,
  } = useForm<HourlyRateFormValues>({
    defaultValues: { label: '', amount: '' },
  });

  useEffect(() => {
    if (open) {
      reset({
        label: editing?.label ?? '',
        amount: editing ? String(editing.amount) : '',
      });
    }
  }, [open, editing, reset]);

  const onFormSubmit = handleSubmit(async (values) => {
    const validation = validateHourlyRateForm(values);
    let hasError = false;
    if (validation.label) {
      setError('label', { message: validation.label });
      hasError = true;
    }
    if (validation.amount) {
      setError('amount', { message: validation.amount });
      hasError = true;
    }
    if (!isHourlyRateLabelUnique(values.label, allRates, editing?.id)) {
      setError('label', { message: t('admin.hourlyRates.labelNotUnique') });
      hasError = true;
    }
    if (hasError) return;
    await onSubmit({ label: values.label.trim(), amount: Number(values.amount) });
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editing ? t('admin.hourlyRates.edit') : t('admin.hourlyRates.new')}
      </DialogTitle>
      <form onSubmit={onFormSubmit} noValidate>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('admin.hourlyRates.label')}
              fullWidth
              autoFocus
              {...register('label')}
              error={Boolean(errors.label)}
              helperText={errors.label?.message}
            />
            <TextField
              label={t('admin.hourlyRates.amount')}
              fullWidth
              type="number"
              inputMode="decimal"
              slotProps={{
                htmlInput: { step: '0.01', min: '0' },
              }}
              {...register('amount')}
              error={Boolean(errors.amount)}
              helperText={errors.amount?.message ?? t('admin.hourlyRates.amountHelp')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="secondary" disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
